// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows outside swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 */
import { A11y, Autoplay, Controller, EffectFade, FreeMode, Navigation, Pagination, Scrollbar } from "swiper/modules";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";

import ArrowNavigation from "@ces/App/Components/XngCarousel/ArrowNavigation";
import { xSwiperShadow, xSwiperStyle } from "@ces/App/Components/XngCarousel/styles";
import { color, mergeWithCss, xScreen } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import pick from "lodash/pick";
import React, { FC, isValidElement, useState } from "react";
import { css } from "styled-components";

export type Props = SwiperProps & {
    extendedStyles?: SwiperStyle;
    lazy?: boolean;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

// project style
const swiperStyle: SwiperStyle = {
    container: {
        css: css`
            ${xSwiperStyle}
            &.with-padding {
                .swiper {
                    margin: -15px;
                    box-sizing: border-box;
                    .swiper-wrapper {
                        padding: 15px;
                    }
                }
            }
            .swiper-wrapper {
                box-sizing: border-box;
            }

            .xng-pagination-container {
                &:not(.visible) {
                    display: none;
                }
                position: relative;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                margin-top: 20px;
                gap: 0;
                & > div,
                & > button {
                    margin: 0;
                }

                &:has(.dots-with-numbers) {
                    height: 30px;
                }

                ${xScreen("gte", "lg")} {
                    position: absolute;
                    bottom: 16px;
                    left: 16px;
                    &:has(> .swiper-scrollbar-horizontal) {
                        position: relative;
                        top: unset;
                        right: unset;
                        bottom: unset;
                        left: unset;
                    }
                }
            }

            .xng-pagination-container > .swiper-scrollbar.swiper-scrollbar-horizontal {
                height: 8px;
                width: 100%;
                position: relative;
                top: unset;
                right: unset;
                bottom: unset;
                left: unset;
                transform: initial;
                display: flex;
                align-items: center;
                background: ${color("common.ui2")};
                border-radius: 4px;
                & > .swiper-scrollbar-drag {
                    background: ${color("common.ui12")};
                    border-radius: 2px;
                    height: 4px;
                }

                &.swiper-scrollbar-lock {
                    display: none;
                }
            }

            .swiper-horizontal > .xng-pagination-container {
                min-height: 10px;
                display: flex !important;
                gap: 4px;
            }

            ${({ isAuto }: any) =>
                isAuto
                    ? css`
                          .swiper-slide {
                              width: auto;
                          }
                      `
                    : ""}

            .swiper-pagination {
                position: relative;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: start;
                flex-wrap: wrap;
                gap: 4px;
                padding-block: 0px;
                &.swiper-pagination-lock {
                    display: none;
                }
                .swiper-pagination-bullet {
                    width: 10px;
                    height: 10px;
                    background: transparent;
                    border: 1px solid ${color("common.ui4")};
                    opacity: 1;
                    margin: 0 !important;
                    &:hover:not(.swiper-pagination-bullet-active) {
                        background: ${color("primary")};
                        border-color: ${color("primary")};
                    }
                    &.dots-with-numbers {
                        display: flex;
                        width: 20px;
                        height: 20px;
                        align-items: center;
                        justify-content: center;
                        font-size: 13px;
                        font-weight: 700;
                        color: ${color("common.ui4")};
                        text-align: center;
                    }
                    &.swiper-pagination-bullet-active {
                        background: ${color("primary")};
                        border-color: ${color("primary")};
                        color: ${color("white")};
                    }
                }
            }
            ${xSwiperShadow}
        `,
    },
};

const XngCarousel: FC<Props> = props => {
    const { children, className, extendedStyles, lazy = true, ...swiperProps } = props;
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));

    let paginationOptions;
    let scrollbarOptions;

    if (swiperProps.pagination) {
        paginationOptions =
            typeof swiperProps.pagination === "boolean"
                ? { el: ".swiper-pagination", clickable: true }
                : swiperProps.pagination;
    }

    if (swiperProps.scrollbar) {
        scrollbarOptions =
            typeof swiperProps.scrollbar === "boolean"
                ? { draggable: true, el: ".swiper-scrollbar" }
                : swiperProps.scrollbar;
    }

    return (
        <StyledWrapper
            {...styles.container}
            className={`carousel-main-wrapper ${className || ""}`}
            {...{ isAuto: props.slidesPerView === "auto" && !props.breakpoints }}
        >
            {/* Add observer/observeParents in-order to re-init in case of content change  */}
            <Swiper
                {...swiperProps}
                modules={[Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay, EffectFade, FreeMode]}
                pagination={paginationOptions}
                scrollbar={scrollbarOptions}
                navigation
                observeParents
                observer
            >
                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props as any, [...swiperSlidePropsKeys]);
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                            {lazy && <div className="swiper-lazy-preloader"></div>}
                        </SwiperSlide>
                    );
                })}

                {(swiperProps.pagination || swiperProps.navigation || swiperProps.scrollbar) && (
                    <StyledWrapper className="xng-pagination-container">
                        {swiperProps.navigation && <ArrowNavigation arrowType="prev" showOnMobile={true} />}
                        {swiperProps.pagination && <div className="swiper-pagination" />}
                        {swiperProps.scrollbar && <div className="swiper-scrollbar" />}
                        {swiperProps.navigation && <ArrowNavigation arrowType="next" showOnMobile={true} />}
                    </StyledWrapper>
                )}
            </Swiper>
        </StyledWrapper>
    );
};

XngCarousel.defaultProps = {
    slidesPerView: "auto",
    navigation: false,
    simulateTouch: true,
    spaceBetween: 20,
    pagination: false,
    scrollbar: false,
    watchOverflow: true,
    updateOnWindowResize: true,
    centerInsufficientSlides: false,
    draggable: true,
    freeMode: false,
};

export default XngCarousel;
